import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, PageHeader, Pill } from "./_shared";
import { toast } from "sonner";
import { RefreshCw, Radio, Activity, BookOpen } from "lucide-react";

const StatusPill = ({ status }) => {
  const map = {
    CONNECTED: ["#10b981", "conectado"],
    CONNECTED_VIA_PROXY: ["#10b981", "conectado (relay)"],
    ACCESS_DENIED: ["#dc2626", "acesso negado"],
    RATE_LIMITED: ["#f59e0b", "rate limit"],
    AUTH_ERROR: ["#dc2626", "auth error"],
    CNJ_UNAVAILABLE: ["#94a3b8", "CNJ 5xx"],
    NETWORK_ERROR: ["#94a3b8", "rede"],
    NO_PUBLICATIONS: ["#10b981", "sem publicações"],
  };
  const [color, label] = map[status] || ["#64748b", status || "—"];
  return (
    <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider"
      style={{ background: `${color}22`, color }}>
      ● {label}
    </span>
  );
};

const KV = ({ k, v }) => (
  <div className="flex justify-between py-1.5 border-b border-white/5 last:border-0">
    <span className="text-[11px] uppercase tracking-wider text-slate-400">{k}</span>
    <span className="text-xs text-slate-200 font-mono max-w-[65%] truncate" title={String(v ?? "")}>{v ?? "—"}</span>
  </div>
);

export default function AdminDjen() {
  const [probe, setProbe] = useState(null);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [manualLoading, setManualLoading] = useState(false);
  const [trCoverage, setTrCoverage] = useState(null);
  const [reparseBusy, setReparseBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const s = await api.get("/djen/status");
      setProbe(s.data);
      const runs = await api.get("/admin/djen/sync-runs").catch(() => ({ data: { items: [] } }));
      setRuns(runs.data?.items || []);
      const cov = await api.get("/admin/jurisprudence/tr-coverage").catch(() => ({ data: null }));
      setTrCoverage(cov.data);
    } catch (e) {
      toast.error("Falha ao carregar diagnósticos DJEN");
    } finally { setLoading(false); }
  };

  const triggerProbe = async () => {
    setManualLoading(true);
    try {
      await api.post("/djen/diagnostic");
      toast.success("Sonda executada");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha na sonda");
    } finally { setManualLoading(false); }
  };

  const triggerSyncAll = async () => {
    setManualLoading(true);
    try {
      await api.post("/cron/djen-sync-all");
      toast.success("Sync enfileirado para todos os advogados");
      setTimeout(load, 3000);
    } catch (e) {
      toast.error("Falha ao disparar sync-all");
    } finally { setManualLoading(false); }
  };

  const reparseTR = async () => {
    setReparseBusy(true);
    try {
      const r = await api.post("/admin/jurisprudence/reparse-tr?limit=2000");
      const d = r.data || {};
      toast.success(
        `Parser TR concluído · ${d.inserted || 0} novas · ${d.updated || 0} atualizadas · ${d.discarded_notice || 0} intimações descartadas`
      );
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha no parser TR");
    } finally { setReparseBusy(false); }
  };

  useEffect(() => { load(); }, []);

  const admin = probe?.admin_detail;
  const lp = admin?.last_probe;
  return (
    <div className="p-3 sm:p-6 lg:p-8" data-testid="admin-djen-page">
      <PageHeader title="🛰️ DJEN · Diagnóstico Técnico"
        subtitle="Sonda, conectividade, egress, sync runs — visível apenas para OWNER"
        right={
          <div className="flex gap-2">
            <button onClick={triggerProbe} disabled={manualLoading || loading}
              data-testid="admin-djen-probe-btn"
              className="h-9 px-3 rounded-lg bg-[#0f1524] hover:bg-[#131a2b] border border-white/10 text-slate-200 text-xs inline-flex items-center gap-2">
              <Radio className="w-3.5 h-3.5"/> Executar sonda
            </button>
            <button onClick={triggerSyncAll} disabled={manualLoading || loading}
              data-testid="admin-djen-syncall-btn"
              className="h-9 px-3 rounded-lg bg-[#d4a24c] hover:bg-[#c48a3e] text-black font-semibold text-xs inline-flex items-center gap-2">
              <RefreshCw className="w-3.5 h-3.5"/> Sync all
            </button>
          </div>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
        <Card>
          <div className="flex items-center justify-between mb-3">
            <div className="text-[11px] uppercase tracking-wider text-slate-400">Saúde pública</div>
            <StatusPill status={probe?.health}/>
          </div>
          <div className="text-sm text-slate-200">{probe?.health_message || "—"}</div>
          <div className="mt-3 text-xs text-slate-400">
            Publicações no cache: <strong className="text-slate-200">{probe?.publications_total ?? 0}</strong>
          </div>
        </Card>

        <Card>
          <div className="text-[11px] uppercase tracking-wider text-slate-400 mb-2">Último probe</div>
          <div className="flex items-center gap-2 mb-2">
            <StatusPill status={lp?.status}/>
            <span className="text-xs text-slate-400">HTTP {lp?.http_status ?? "—"}</span>
          </div>
          <KV k="Egress IP" v={lp?.egress_ip}/>
          <KV k="Latency" v={lp?.latency_ms ? `${lp.latency_ms} ms` : "—"}/>
          <KV k="Checked at" v={lp?.checked_at?.slice(0, 19).replace("T", " ")}/>
          <KV k="Proxy usado" v={lp?.proxy_used ? "sim" : "não"}/>
        </Card>

        <Card>
          <div className="text-[11px] uppercase tracking-wider text-slate-400 mb-2">Configuração</div>
          <KV k="DJEN_PROXY_URL" v={admin?.proxy_configured ? "configurado" : "ausente"}/>
          <KV k="DJEN_MOCK_MODE" v={admin?.mock_mode ? "ligado" : "desligado"}/>
          <KV k="Endpoint" v="comunicaapi.pje.jus.br/api/v1"/>
          <KV k="Último sucesso" v={admin?.last_success_at?.slice(0, 19).replace("T", " ") ?? "nunca"}/>
          <KV k="Último erro" v={admin?.last_error_at?.slice(0, 19).replace("T", " ") ?? "—"}/>
        </Card>
      </div>

      <div className="mt-6">
        <Card>
          <div className="flex items-center justify-between mb-3">
            <div className="text-[11px] uppercase tracking-wider text-slate-400">Rate limit / headers</div>
            <Activity className="w-3.5 h-3.5 text-slate-500"/>
          </div>
          {lp?.rate_limit_headers && Object.keys(lp.rate_limit_headers).length > 0 ? (
            <div className="grid grid-cols-2 gap-x-6">
              {Object.entries(lp.rate_limit_headers).map(([k, v]) => (
                <KV key={k} k={k} v={String(v)}/>
              ))}
            </div>
          ) : (
            <div className="text-xs text-slate-500">Nenhum header capturado.</div>
          )}
        </Card>
      </div>

      <div className="mt-6">
        <Card>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <BookOpen className="w-3.5 h-3.5 text-[#d4a24c]"/>
              <div className="text-[11px] uppercase tracking-wider text-slate-300 font-semibold">
                Ementa Oficial Parser · Tribunais Regionais
              </div>
            </div>
            <button onClick={reparseTR} disabled={reparseBusy}
              data-testid="btn-reparse-tr"
              className="px-3 py-1.5 rounded-lg bg-[#d4a24c] hover:bg-[#c69034] disabled:opacity-50 text-[#0b0f1a] text-[11px] font-semibold uppercase tracking-wider inline-flex items-center gap-1.5">
              {reparseBusy ? <RefreshCw className="w-3 h-3 animate-spin"/> : <RefreshCw className="w-3 h-3"/>}
              {reparseBusy ? "Processando…" : "Reprocessar TRs"}
            </button>
          </div>
          <div className="text-[11px] text-slate-400 mb-3">
            Extrai ementas oficiais das publicações DJEN de TRF/TRT/TRE com strip de HTML,
            marcadores estendidos (EMENTA, ACÓRDÃO, DECISÃO MONOCRÁTICA, DESPACHO/DECISÃO,
            SENTENÇA, VOTO) e descarte automático de meras intimações. Idempotente.
          </div>
          {!trCoverage ? (
            <div className="text-xs text-slate-500">Sem dados de cobertura ainda.</div>
          ) : (trCoverage.courts || []).length === 0 ? (
            <div className="text-xs text-slate-500" data-testid="tr-coverage-empty">
              Nenhuma publicação de Tribunal Regional coletada ainda.
            </div>
          ) : (
            <div className="overflow-x-auto" data-testid="tr-coverage-table">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-white/5">
                    <th className="py-2 pr-3">Tribunal</th>
                    <th className="py-2 pr-3">Pubs DJEN</th>
                    <th className="py-2 pr-3">Ementas parseadas</th>
                    <th className="py-2 pr-3">Cobertura</th>
                  </tr>
                </thead>
                <tbody>
                  {trCoverage.courts.map((c) => (
                    <tr key={c.court} className="border-b border-white/5 last:border-0"
                      data-testid={`tr-court-${c.court}`}>
                      <td className="py-2 pr-3 text-slate-200 font-mono">{c.court}</td>
                      <td className="py-2 pr-3 text-slate-300">{c.djen_publications}</td>
                      <td className="py-2 pr-3 text-slate-300">{c.parsed_ementas}</td>
                      <td className="py-2 pr-3">
                        <span className={`font-semibold ${c.coverage_pct >= 60 ? "text-emerald-400" : c.coverage_pct >= 20 ? "text-amber-400" : "text-slate-500"}`}>
                          {c.coverage_pct}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="text-[11px] text-slate-400">
                    <td className="py-2 pr-3 uppercase tracking-wider">Total</td>
                    <td className="py-2 pr-3">{trCoverage.total_publications}</td>
                    <td className="py-2 pr-3">{trCoverage.total_parsed}</td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </Card>
      </div>

      <div className="mt-6">
        <Card>
          <div className="flex items-center justify-between mb-3">
            <div className="text-[11px] uppercase tracking-wider text-slate-400">Sync runs recentes</div>
            <span className="text-[10px] text-slate-500">{runs.length} eventos</span>
          </div>
          {runs.length === 0 ? (
            <div className="text-xs text-slate-500">Sem execuções registradas ainda.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-white/5">
                    <th className="py-2 pr-3">Quando</th>
                    <th className="py-2 pr-3">Tipo</th>
                    <th className="py-2 pr-3">Processados</th>
                    <th className="py-2 pr-3">Enfileirados</th>
                  </tr>
                </thead>
                <tbody>
                  {runs.map((r, i) => (
                    <tr key={i} className="border-b border-white/5 last:border-0" data-testid={`admin-djen-run-${i}`}>
                      <td className="py-2 pr-3 text-slate-300 font-mono">{(r.at || r.started_at || "").slice(0, 19).replace("T", " ")}</td>
                      <td className="py-2 pr-3"><Pill>{r.kind || "run"}</Pill></td>
                      <td className="py-2 pr-3 text-slate-200">{r.processed ?? "—"}</td>
                      <td className="py-2 pr-3 text-slate-200">{r.enqueued ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
